import React from 'react';
import { Box, Grid, Paper, Typography, Stack, Chip, Button, Skeleton, Tooltip, Dialog, DialogTitle, DialogContent, Table, TableHead, TableRow, TableCell, TableBody, IconButton } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InventoryIcon from '@mui/icons-material/Inventory2';
import GroupsIcon from '@mui/icons-material/Groups';
import DiamondIcon from '@mui/icons-material/Diamond';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import { useGetProductsQuery, useGetTopProductAdoptionQuery } from '../../app/services/product';
import { useGetProductSubscriptionsQuery, useLazyGetSubscriptionFeaturesQuery } from '../../app/services/subscription';
import { useGetDealersDirectQuery, useGetDealersByProductQuery, useGetDealersAddedSinceQuery, useGetDealerOnboardingActivityQuery } from '../../app/services/dealer';
import { useGetProductAddOnsQuery } from '../../app/services/addons';
import { useGetProductFeaturesQuery } from '../../app/services/product';
import { mapDmsFilterToEnumValue, DMS_ENUM_MAP } from '../../util/dmsMapping';
import DealerDetailsDialog from '../Dealers/components/DealerDetailsDialog';
import DealerLocationDistributionCard from './components/DealerLocationDistributionCard';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, Legend } from 'recharts';

const MetricCard: React.FC<{ icon: React.ReactNode; label: string; value: string | number; sub?: string }>
  = ({ icon, label, value, sub }) => (
  <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
    <Stack direction="row" spacing={2} alignItems="center">
      <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main' }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ lineHeight: 1.2 }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </Box>
    </Stack>
  </Paper>
);

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: productsData, isLoading: loadingProducts } = useGetProductsQuery({ page: 1, pageSize: 500 }, { refetchOnMountOrArgChange: true });
  const products = (productsData?.data || []).filter((p: any) => p.productName !== 'IDMSPortal');

  const { data: productSubs, isLoading: loadingSubs } = useGetProductSubscriptionsQuery();
  const totalTiers = React.useMemo(() => {
    if (!productSubs) return 0;
    return productSubs.reduce((acc: number, p: any) => acc + (p.subscriptions?.length || 0), 0);
  }, [productSubs]);

  const { data: dealersResp, isFetching: loadingDealers } = useGetDealersDirectQuery({
    dmsTypes: mapDmsFilterToEnumValue('All'),
    searchText: '',
    page: 1,
    pageSize: 1,
    case: 'All',
  }, { refetchOnMountOrArgChange: true });
  const totalDealers = dealersResp?.totalRecordsCount ?? 0;

  const byProduct = React.useMemo(() => {
    const entries: { name: string; productId: string; tiers: number }[] = [];
    const subsById = new Map<string, any>((productSubs || []).map((g: any) => [String(g.productId), g]));
    (products || []).forEach((p: any) => {
      const productId = String(p.id ?? p.productId);
      const name = p.productName ?? p.name ?? '(Unnamed)';
      const group = subsById.get(productId);
      const tiers = group?.subscriptions?.length || 0;
      entries.push({ name, productId, tiers });
    });
    return entries.sort((a, b) => (b.tiers - a.tiers) || a.name.localeCompare(b.name));
  }, [products, productSubs]);

  const loading = loadingProducts || loadingSubs || loadingDealers;

  const sampled = React.useMemo(() => products.slice(0, 3), [products]);

  const [tiersOpen, setTiersOpen] = React.useState(true);

  const ProductMetrics: React.FC<{ productId: string }> = ({ productId }) => {
    const { data: featuresResp } = useGetProductFeaturesQuery({ productId, page: 1, pageSize: 1000 });
    const productFeatures = React.useMemo(() => (featuresResp?.data || []).map((f: any) => String(f.id)), [featuresResp]);

    const subsForProduct = React.useMemo(() => (productSubs || []).find(p => p.productId === productId)?.subscriptions || [], [productSubs, productId]);
    const [assignedIds, setAssignedIds] = React.useState<Set<string>>(new Set());
    const [trigger] = useLazyGetSubscriptionFeaturesQuery();

    React.useEffect(() => {
      let mounted = true;
      const run = async () => {
        try {
          const all = await Promise.all((subsForProduct || []).map((s: any) => trigger(s.productSubscriptionId).unwrap().catch(() => [])));
          const set = new Set<string>();
          all.forEach((arr: any[]) => arr.forEach((f: any) => set.add(String(f.id))));
          if (mounted) setAssignedIds(set);
        } catch {}
      };
      run();
      return () => { mounted = false; };
    }, [subsForProduct, trigger]);

    const { data: addOns } = useGetProductAddOnsQuery(productId);
    const addOnIds = React.useMemo(() => new Set<string>((addOns || []).map((a: any) => String(a.featureId))), [addOns]);
    const onTierCount = React.useMemo(() => assignedIds.size, [assignedIds]);
    const unassignedCount = React.useMemo(() => {
      const all = new Set<string>(productFeatures || []);
      const covered = new Set<string>([...assignedIds, ...addOnIds]);
      let c = 0; all.forEach((fid) => { if (!covered.has(fid)) c++; });
      return c;
    }, [productFeatures, assignedIds, addOnIds]);
    const addOnsCount = React.useMemo(() => addOnIds.size, [addOnIds]);

    return (
      <Stack direction="column" spacing={0.5} sx={{ mt: 0.5 }}>
        <Tooltip title="Features assigned to any subscription tier for this product">
          <Chip size="small" variant="outlined" label={`On Tier: ${onTierCount}`} />
        </Tooltip>
        <Tooltip title="Features not assigned to any tier or add-on">
          <Chip size="small" variant="outlined" label={`Unassigned: ${unassignedCount}`} />
        </Tooltip>
        <Tooltip title="Features sold as add-ons for this product">
          <Chip size="small" color="warning" variant="outlined" label={`Add-Ons: ${addOnsCount}`} />
        </Tooltip>
      </Stack>
    );
  };

  const { data: adoption, isFetching: loadingAdoption } = useGetTopProductAdoptionQuery(6);

  const { data: recentActivity, isFetching: loadingRecent } = useGetDealerOnboardingActivityQuery(14);
  const [recentOpen, setRecentOpen] = React.useState(false);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [detailsDealerId, setDetailsDealerId] = React.useState<number | undefined>(undefined);
  const [detailsDealerName, setDetailsDealerName] = React.useState<string | undefined>(undefined);
  const openDetails = (dealerId: number, dealerName?: string) => { setDetailsDealerId(dealerId); setDetailsDealerName(dealerName); setDetailsOpen(true); };
  const onboardedCount = React.useMemo(() => (recentActivity || []).filter((a: any) => a.actionType === 'Assign' && a.firstTimeOnboard).length, [recentActivity]);
  const tierChangeCount = React.useMemo(() => (recentActivity || []).filter((a: any) => a.actionType === 'Change').length, [recentActivity]);

  const activityChartData = React.useMemo(() => {
    const today = new Date();
    const days: { key: string; label: string }[] = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const key = d.toISOString().slice(0, 10); // YYYY-MM-DD
      const label = `${d.getMonth() + 1}/${d.getDate()}`;
      days.push({ key, label });
    }
    const counts = new Map<string, { onboarded: number; changes: number }>();
    days.forEach((d) => counts.set(d.key, { onboarded: 0, changes: 0 }));
    (recentActivity || []).forEach((r: any) => {
      const date = new Date(r.recordedDate);
      const key = date.toISOString().slice(0, 10);
      const entry = counts.get(key);
      if (!entry) return;
      if (r.actionType === 'Assign' && r.firstTimeOnboard) entry.onboarded += 1;
      else if (r.actionType === 'Change') entry.changes += 1;
    });
    return days.map((d) => ({ date: d.label, onboarded: counts.get(d.key)!.onboarded, changes: counts.get(d.key)!.changes }));
  }, [recentActivity]);

  const [helpOpen, setHelpOpen] = React.useState(false);

  return (
    <Box sx={{ width: '100%', py: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>Key Metrics</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Create and edit product features across all products">
            <Button variant="outlined" size="small" onClick={() => navigate('/features')} endIcon={<ArrowForwardIcon />}>Features Catalog</Button>
          </Tooltip>
          <Tooltip title="Design subscription tiers and assign features or mark some as add-ons">
            <Button variant="outlined" size="small" onClick={() => navigate('/subscriptions')} endIcon={<ArrowForwardIcon />}>Design Tiers</Button>
          </Tooltip>
          <Tooltip title="Manage products metadata and lifecycle">
            <Button variant="outlined" size="small" onClick={() => navigate('/products')} endIcon={<ArrowForwardIcon />}>Products</Button>
          </Tooltip>
          <Tooltip title="Browse dealerships and their assignments">
            <Button variant="outlined" size="small" onClick={() => navigate('/dealers')} endIcon={<ArrowForwardIcon />}>Dealers</Button>
          </Tooltip>
        </Stack>
      </Stack>

      {loading ? (
        <Grid container spacing={1.5}>
          {[0,1,2].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                <Skeleton variant="rounded" width={160} height={24} />
                <Skeleton variant="rounded" width={100} height={32} sx={{ mt: 1 }} />
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={4}>
            <MetricCard icon={<InventoryIcon />} label="Products" value={products.length} sub="Excludes internal products" />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MetricCard icon={<DiamondIcon />} label="Subscription Tiers" value={totalTiers} sub={productSubs ? `${productSubs.length} products` : undefined} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MetricCard icon={<GroupsIcon />} label="Dealers" value={totalDealers} sub="Total across all DMS types" />
          </Grid>
        </Grid>
      )}

      <Paper elevation={0} sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" fontWeight={700}>Tiers and Features</Typography>
            <Tooltip title="Tiers are subscription plans while features are the capabilities available in those plans">
              <IconButton size="small" onClick={() => setHelpOpen(true)} aria-label="Help">
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <TrendingUpIcon color="primary" />
          </Stack>
          <IconButton size="small" onClick={() => setTiersOpen(!tiersOpen)} aria-label={tiersOpen ? 'Collapse' : 'Expand'}>
            {tiersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>
        {tiersOpen && (
          <>
            {loadingSubs ? (
              <Skeleton variant="rounded" height={120} />
            ) : byProduct.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No products found.</Typography>
            ) : (
              <Grid container spacing={1}>
                {byProduct.slice(0, 12).map((p) => (
                  <Grid item xs={12} sm={6} md={3} lg={2} xl={2} key={p.productId}>
                    <Paper variant="outlined" sx={{ p: 1, height: '100%', bgcolor: (theme) => theme.palette.action.hover }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" noWrap sx={{ pr: 1, maxWidth: '70%' }}>{p.name}</Typography>
                        <Chip size="small" color="primary" variant="outlined" label={`${p.tiers} tier${p.tiers !== 1 ? 's' : ''}`} />
                      </Stack>
                      <Box sx={{ mt: 1 }}>
                        <ProductMetrics productId={p.productId} />
                      </Box>
                      {p.tiers === 0 && (
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button size="small" variant="text" onClick={() => navigate(`/subscriptions?productId=${p.productId}`)}>
                            Design tiers
                          </Button>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Paper>
      <Paper elevation={0} sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6" fontWeight={700}>Customer Success</Typography>
        </Stack>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Top products by number of dealers</Typography>
              {loadingAdoption ? (
                <Skeleton variant="rounded" height={120} />
              ) : (
                <Stack spacing={1}>
                  {(() => {
                    const list = adoption ?? [];
                    const max = Math.max(1, ...list.map((x: any) => x.dealersCount || 0));
                    return list.map((p: any) => (
                      <Box key={p.productId}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="body2" noWrap sx={{ pr: 1 }}>{p.productName}</Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" color="primary" variant="outlined" label={`${p.dealersCount} dealers`} />
                            <Button size="small" onClick={() => navigate(`/dealers?productId=${p.productId}&productName=${encodeURIComponent(p.productName)}`)}>View all</Button>
                          </Stack>
                        </Stack>
                        <Box sx={{ mt: 0.5, height: 6, borderRadius: 1, bgcolor: 'action.hover', overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${Math.round((p.dealersCount || 0) / max * 100)}%`, bgcolor: 'primary.main', opacity: 0.35 }} />
                        </Box>
                      </Box>
                    ));
                  })()}
                </Stack>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>Dealer onboarding activity (last 14 days)</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title="Dealers newly onboarded to a product">
                    <Chip size="small" color="secondary" variant="outlined" label={`Onboarded: ${loadingRecent ? '…' : onboardedCount}`} />
                  </Tooltip>
                  <Tooltip title="Dealers whose subscription tier changed">
                    <Chip size="small" color="default" variant="outlined" label={`Tier changes: ${loadingRecent ? '…' : tierChangeCount}`} />
                  </Tooltip>
                  <Button size="small" onClick={() => setRecentOpen(true)}>View list</Button>
                </Stack>
              </Stack>
              <Typography variant="caption" color="text.secondary">Reflects both onboarding and tier changes.</Typography>
              <Box sx={{ mt: 1, height: 220 }}>
                {loadingRecent ? (
                  <Skeleton variant="rounded" height={200} />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={activityChartData} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <RTooltip />
                      <Legend />
                      <Bar dataKey="onboarded" name="Onboarded" fill="#7E57C2" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="changes" name="Tier changes" fill="#90A4AE" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
      <Box sx={{ mt: 2 }}>
        <DealerLocationDistributionCard />
      </Box>
      <Dialog open={recentOpen} onClose={() => setRecentOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Dealer onboarding activity (last 14 days)</DialogTitle>
        <DialogContent>
          {loadingRecent ? (
            <Skeleton variant="rounded" height={180} />
          ) : !recentActivity || recentActivity.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No recent onboarding activity.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Dealer</TableCell>
                  <TableCell>DMS</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(recentActivity || []).map((r: any) => (
                  <TableRow key={`${r.dealerId}-${r.productId}-${r.recordedDate}`}>
                    <TableCell>{r.dealerName ?? r.dealerId}</TableCell>
                    <TableCell>{(() => { const entry = Object.entries(DMS_ENUM_MAP).find(([_, v]) => v === (r.dmsType ?? -1)); return entry ? entry[0] : String(r.dmsType ?? '-') })()}</TableCell>
                    <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 420 }}>{r.address ?? '-'}</Typography></TableCell>
                    <TableCell>{r.productName ?? '-'}</TableCell>
                    <TableCell>{r.firstTimeOnboard && r.actionType === 'Assign' ? 'Onboarded' : (r.actionType === 'Change' ? 'Tier change' : r.actionType)}</TableCell>
                    <TableCell>{new Date(r.recordedDate).toLocaleString()}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => openDetails(r.dealerId, r.dealerName)}>Open</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
      <DealerDetailsDialog open={detailsOpen} onClose={() => setDetailsOpen(false)} dealerId={detailsDealerId} dealerName={detailsDealerName} />
    </Box>
  );
};

export default DashboardPage;
